const PLACEHOLDER_OFFSET = -9999999999;
export interface BackendStripData {
  /** Strip ID (1-based as per backend expectation) */
  strip_id: number;

  /** Strip offset distance in meters from base AB-line */
  offset: number;

  /** Calculated strip area in square meters */
  area: number;

  /** Calculated strip length in meters */
  strip_length: number;

  /** Calculated strip width in meters */
  strip_width: number;
}

export interface BackendStripsData {
  /** Whether strips mode is enabled */
  is_strip: boolean;

  /** Index of the selected AB-line in the ab_line array */
  selected_ab_line_index: number;

  /** Individual strip data with calculated values */
  strips: BackendStripData[];
}

/**
 * Frontend strip data structure (camelCase for internal use)
 */
export interface StripData {
  /** Strip index (0-based) */
  index: number;

  /** Strip offset distance in meters from base AB-line */
  offset: number;

  /** Calculated strip area in square meters */
  area: number;

  /** Calculated strip length in meters */
  stripLength: number;

  /** Calculated strip width in meters */
  stripWidth: number;
}

/**
 * Frontend strips data structure (camelCase for internal use)
 */
export interface OptimizedStripsData {
  /** Whether strips mode is enabled */
  isStripMode: boolean;

  /** Index of the selected AB-line in the ab_line array */
  selectedAbLineIndex: number;

  /** Individual strip data with calculated values */
  strips: StripData[];
}

// ============================================================================
// FRONTEND DATA STRUCTURE (Full, with derived properties for UI)
// ============================================================================

/**
 * Complete strips data structure used in frontend state
 * This includes derived properties for backward compatibility and UI convenience
 */
export interface FullStripsData extends OptimizedStripsData {
  /** Legacy: Number of strips (derived from strips.length) */
  count?: number;

  /** Default spacing between strips (derived from strips data) */
  spacing?: number;

  /** Selected AB-line details (derived from selectedAbLineIndex) */
  selectedAbLine?: {
    name: string;
    gridWidth: number;
    geojson: any;
  };

  /** Legacy: Array of just areas (derived from strips[].area for backward compatibility) */
  areas?: number[];

  /** Legacy: Array of offsets (derived from strips[].offset for backward compatibility) */
  offsets?: number[];

  /** Legacy: Strip details (same as strips but with different name for backward compatibility) */
  stripDetails?: Array<{
    index: number;
    area: number;
    stripLength: number;
    stripWidth: number;
  }>;

  /** Legacy: Metadata fields for backward compatibility */
  createdAt?: string;
  version?: string;

  /** Flag to indicate if strips were applied from popup */
  appliedFromPopup?: boolean;
}

export const transformFromBackendFormat = (
  backendData: { strip_design: BackendStripsData },
  abLineArray: any[]
): FullStripsData => {
  if (!backendData?.strip_design) {
    throw new Error("Invalid backend strips data - missing strip_design");
  }

  const stripDesign = backendData.strip_design;

  // Convert backend strips to frontend format
  const frontendStrips: StripData[] = stripDesign.strips.map(
    (backendStrip: BackendStripData) => ({
      index: backendStrip.strip_id - 1, // Convert from 1-based to 0-based
      offset: backendStrip.offset,
      area: backendStrip.area,
      stripLength: backendStrip.strip_length,
      stripWidth: backendStrip.strip_width,
    })
  );

  // Create optimized internal format first
  const optimizedData: OptimizedStripsData = {
    isStripMode: stripDesign.is_strip,
    selectedAbLineIndex: stripDesign.selected_ab_line_index,
    strips: frontendStrips,
  };

  // Then hydrate to full format for frontend use
  return hydrateStripsDataForFrontend(optimizedData, abLineArray);
};

/**
 * Transform to backend format with snake_case naming
 */
export const transformToBackendFormat = (
  data: OptimizedStripsData
): { strip_design: BackendStripsData } => {
  if (!validateOptimizedStripsData(data)) {
    throw new Error("Invalid strips data for backend transformation");
  }

  const backendStrips: BackendStripData[] = data.strips.map((strip, index) => ({
    strip_id: index + 1, // Backend expects 1-based IDs
    offset: strip.offset,
    area: strip.area,
    strip_length: strip.stripLength,
    strip_width: strip.stripWidth,
  }));

  return {
    strip_design: {
      is_strip: data.isStripMode,
      selected_ab_line_index: data.selectedAbLineIndex,
      strips: backendStrips,
    },
  };
};

/**
 * Transform full strips data to optimized format for backend
 */
export const optimizeStripsDataForBackend = (
  fullData: FullStripsData
): OptimizedStripsData => {
  if (!fullData?.isStripMode) {
    throw new Error("Invalid strips data provided");
  }

  // Always prioritize offsets array for correct offset values
  let strips: StripData[];

  if (fullData.stripDetails && fullData.offsets) {
    // Construct from legacy fields (stripDetails + offsets) - this is the most reliable approach
    if (fullData.stripDetails.length !== fullData.offsets.length) {
      console.warn(
        `⚠️ stripDetails (${fullData.stripDetails.length}) and offsets (${fullData.offsets.length}) array lengths don't match - will calculate missing offsets`
      );
    }

    // Always use all stripDetails, use placeholder offset for strips beyond available offsets
    strips = fullData.stripDetails.map((detail, index) => {
      let offset = fullData.offsets?.[index];

      // If offset is missing (strip index beyond available offsets), use special placeholder
      if (offset === undefined || offset === null) {
        // Use special placeholder offset that will be ignored during reconstruction
        offset = PLACEHOLDER_OFFSET;
        console.warn(
          `🎯 Using placeholder offset (${PLACEHOLDER_OFFSET}) for strip ${index} beyond available offsets`
        );
      }

      return {
        index: detail.index,
        offset,
        area: detail.area,
        stripLength: detail.stripLength,
        stripWidth: detail.stripWidth,
      };
    });
    // Using stripDetails + offsets (with calculated missing offsets) for strips data
  } else if (fullData.strips && fullData.strips.length > 0) {
    // Fallback to strips array if legacy fields not available
    strips = fullData.strips;
    // Using existing strips array - offsets may be incorrect
  } else {
    throw new Error(
      "No strip data available to optimize - missing both stripDetails+offsets and strips array"
    );
  }

  const optimized: OptimizedStripsData = {
    isStripMode: fullData.isStripMode,
    selectedAbLineIndex: fullData.selectedAbLineIndex,
    strips,
  };

  // Validation
  if (!validateOptimizedStripsData(optimized)) {
    throw new Error("Optimized strips data failed validation");
  }

  return optimized;
};

/**
 * Hydrate optimized strips data back to full format for frontend
 */
export const hydrateStripsDataForFrontend = (
  optimizedData: OptimizedStripsData,
  abLineArray: any[]
): FullStripsData => {
  if (!validateOptimizedStripsData(optimizedData)) {
    throw new Error("Invalid optimized strips data");
  }

  // Get selected AB-line from array
  const selectedAbLine = abLineArray[optimizedData.selectedAbLineIndex];
  if (!selectedAbLine) {
    throw new Error(
      `AB-line at index ${optimizedData.selectedAbLineIndex} not found`
    );
  }

  // Calculate spacing from strips offsets (exclude placeholder offsets)
  let spacing: number;
  const realOffsets = optimizedData.strips
    .map((strip) => strip.offset)
    .filter((offset) => offset !== PLACEHOLDER_OFFSET) // Exclude placeholder offsets
    .sort((a, b) => a - b);

  if (realOffsets.length > 1) {
    // Calculate average spacing from consecutive real offsets
    const spacings = realOffsets
      .slice(1)
      .map((offset, i) => offset - realOffsets[i]);
    spacing = spacings.reduce((sum, s) => sum + s, 0) / spacings.length;
  } else {
    // Fallback to AB-line gridWidth
    spacing = selectedAbLine.gridWidth || selectedAbLine.grid_width || 100;
  }

  // Create legacy arrays for backward compatibility
  const areas = optimizedData.strips.map((strip) => strip.area);
  // For offsets array, exclude placeholder offsets (keep only real offset values)
  const offsets = optimizedData.strips
    .map((strip) => strip.offset)
    .filter((offset) => offset !== PLACEHOLDER_OFFSET);
  const stripDetails = optimizedData.strips.map((strip) => ({
    index: strip.index,
    area: strip.area,
    stripLength: strip.stripLength,
    stripWidth: strip.stripWidth,
  }));

  const hydrated: FullStripsData = {
    ...optimizedData,
    count: optimizedData.strips.length, // Derived from strips array
    spacing,
    selectedAbLine: {
      name: selectedAbLine.name,
      gridWidth: selectedAbLine.gridWidth || selectedAbLine.grid_width || 100,
      geojson: selectedAbLine.geojson,
    },
    // Legacy arrays for backward compatibility
    areas,
    offsets,
    stripDetails,
    // Legacy metadata with default values
    createdAt: new Date().toISOString(),
    version: "1.0",
  };

  return hydrated;
};

/**
 * Validate optimized strips data
 */
export const validateOptimizedStripsData = (
  data: OptimizedStripsData
): boolean => {
  if (!data) {
    console.error("❌ Strips data is null/undefined");
    return false;
  }

  // Required fields
  const requiredFields: Array<keyof OptimizedStripsData> = [
    "isStripMode",
    "selectedAbLineIndex",
    "strips",
  ];

  for (const field of requiredFields) {
    if (data[field] === undefined || data[field] === null) {
      console.error(`❌ Missing required field: ${field}`);
      return false;
    }
  }

  // Type validations
  if (typeof data.isStripMode !== "boolean") {
    console.error("❌ isStripMode must be boolean");
    return false;
  }

  if (
    !Number.isInteger(data.selectedAbLineIndex) ||
    data.selectedAbLineIndex < 0
  ) {
    console.error(
      "❌ selectedAbLineIndex must be non-negative integer, got:",
      data.selectedAbLineIndex
    );
    return false;
  }

  if (!Array.isArray(data.strips) || data.strips.length === 0) {
    console.error("❌ strips must be non-empty array, got:", data.strips);
    return false;
  }

  // Validate each strip
  for (let i = 0; i < data.strips.length; i++) {
    const strip = data.strips[i];
    if (!strip || typeof strip !== "object") {
      console.error(`❌ strips[${i}] must be an object`);
      return false;
    }

    const requiredStripFields = [
      "index",
      "offset",
      "area",
      "stripLength",
      "stripWidth",
    ];
    for (const field of requiredStripFields) {
      if (
        typeof strip[field as keyof StripData] !== "number" ||
        Number.isNaN(strip[field as keyof StripData])
      ) {
        console.error(`❌ strips[${i}].${field} must be a valid number`);
        return false;
      }
    }
  }

  // Optimized strips data validation passed
  return true;
};

/**
 * Calculate size reduction from optimization
 */
export const getOptimizationStats = (
  originalData: FullStripsData,
  optimizedData: OptimizedStripsData
): {
  originalSize: number;
  optimizedSize: number;
  reduction: number;
  reductionPercentage: string;
  removedFields: string[];
  keptFields: string[];
  canReconstruct: boolean;
  note: string;
} => {
  const originalSize = JSON.stringify(originalData).length;
  const optimizedSize = JSON.stringify(optimizedData).length;
  const reduction = originalSize - optimizedSize;
  const reductionPercentage = ((reduction / originalSize) * 100).toFixed(1);

  const removedFields = [
    "count", // Derived from strips.length
    "spacing", // Calculated from strips offsets
    "selectedAbLine", // Retrieved from ab_line array using selectedAbLineIndex
    "areas", // Redundant with strips[].area
    "offsets", // Redundant with strips[].offset
    "stripDetails", // Same data as strips array
    "createdAt", // Metadata not needed for functionality
    "version", // Metadata not needed for functionality
  ];

  const keptFields = [
    "isStripMode", // Essential configuration
    "selectedAbLineIndex", // Reference to AB-line
    "strips", // Calculated strip data (expensive to recalculate)
  ];

  return {
    originalSize,
    optimizedSize,
    reduction,
    reductionPercentage: `${reductionPercentage}%`,
    removedFields,
    keptFields,
    canReconstruct: true,
    note: "All removed fields can be derived or reconstructed from the optimized data",
  };
};

// ============================================================================
// EXAMPLE TRANSFORMATIONS
// ============================================================================

/**
 * Example of current data structure transformation
 */
export const exampleTransformation = (): {
  currentData: any;
  optimizedInternal: OptimizedStripsData;
  backendFormat: { strip_design: BackendStripsData };
} => {
  // Current structure (what you showed in the request)
  const currentData = {
    isStripMode: true,
    count: 2,
    selectedAbLineIndex: 1,
    offsets: [0, 100], // Fixed: should have 2 offsets for 2 strips
    spacing: 100,
    createdAt: "2025-10-03T05:51:50.885Z",
    version: "1.0",
    selectedAbLine: {
      name: "A/B Line 2",
      gridWidth: 100,
      geojson: {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [
            [-58.06298793003763, -14.365427566801003],
            [-58.05627466765214, -14.36622084885897],
          ],
        },
      },
    },
    areas: [3667449.7042322564, 869368.0946261258], // REDUNDANT
    stripDetails: [
      {
        index: 0,
        area: 3667449.7042322564, // DUPLICATE of areas[0]
        stripLength: 2691.46,
        stripWidth: 2139.64,
      },
      {
        index: 1,
        area: 869368.0946261258, // DUPLICATE of areas[1]
        stripLength: 2070.51,
        stripWidth: 771.32,
      },
    ],
  };

  // Optimized for internal use
  const optimizedInternal: OptimizedStripsData = {
    isStripMode: true,
    selectedAbLineIndex: 1,
    strips: [
      {
        index: 0,
        offset: 0,
        area: 3667449.7042322564,
        stripLength: 2691.46,
        stripWidth: 2139.64,
      },
      {
        index: 1,
        offset: 100,
        area: 869368.0946261258,
        stripLength: 2070.51,
        stripWidth: 771.32,
      },
    ],
  };

  // Transform to backend format (what you actually send to API)
  const backendFormat = {
    strip_design: {
      is_strip: true,
      selected_ab_line_index: 1,
      strips: [
        {
          strip_id: 1,
          offset: 0,
          area: 3667449.7042322564,
          strip_length: 2691.46,
          strip_width: 2139.64,
        },
        {
          strip_id: 2,
          offset: 100,
          area: 869368.0946261258,
          strip_length: 2070.51,
          strip_width: 771.32,
        },
      ],
    },
  };

  return { currentData, optimizedInternal, backendFormat };
};

export default {
  transformFromBackendFormat,
  transformToBackendFormat,
  optimizeStripsDataForBackend,
  hydrateStripsDataForFrontend,
  validateOptimizedStripsData,
  getOptimizationStats,
  exampleTransformation,
};
